import React from 'react';

function fmtDay(dateStr: string): string {
  const d = new Date(dateStr + 'T12:00:00');
  return String(d.getDate());
}

function fmtMonth(dateStr: string): string {
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('fr-FR', { month: 'short' }).replace('.', '').toUpperCase();
}

function fmtDateRange(start: string, end?: string): string {
  if (!end || end === start) return '';
  const s = new Date(start + 'T12:00:00');
  const e = new Date(end + 'T12:00:00');
  const diffDays = Math.round((e.getTime() - s.getTime()) / 86400000);
  if (diffDays > 1) {
    return `→ ${e.getDate()} ${e.toLocaleDateString('fr-FR', { month: 'short' }).replace('.', '').toUpperCase()}`;
  }
  return '';
}

export function EventCard({
  image,
  dateStart,
  dateEnd,
  tag,
  title,
  subtitle,
  onClick,
  style,
  className,
}: {
  image?: string;
  dateStart?: string;
  dateEnd?: string;
  tag?: string;
  title: string;
  subtitle?: string;
  onClick?: () => void;
  style?: React.CSSProperties;
  className?: string;
}) {
  const bgStyle: React.CSSProperties = image
    ? { backgroundImage: `linear-gradient(transparent 25%, rgba(0,0,0,0.72)), url(${image})` }
    : { background: 'linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%)' };

  const endLabel = dateStart && dateEnd ? fmtDateRange(dateStart, dateEnd) : '';

  return (
    <div
      className={className}
      onClick={onClick}
      style={{
        /* layout */
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        /* sizing — box-sizing MUST be border-box so padding doesn't add to width */
        boxSizing: 'border-box',
        width: '100%',
        minHeight: 160,
        padding: 14,
        /* visuals */
        borderRadius: 'var(--radius-lg, 20px)',
        overflow: 'hidden',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        cursor: onClick ? 'pointer' : 'default',
        fontFamily: 'var(--font-family, system-ui)',
        ...bgStyle,
        /* caller can override height, border-radius, etc. */
        ...style,
      }}
    >
      {/* Top row: date badge (left) + tag (right) */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 8,
        minWidth: 0,
      }}>
        {/* Date badge — flex-shrink: 0 so it's never compressed */}
        {dateStart ? (
          <div style={{
            background: '#fff',
            borderRadius: 10,
            padding: '6px 10px',
            textAlign: 'center',
            flexShrink: 0,
          }}>
            <span style={{ display: 'block', fontSize: 20, fontWeight: 900, color: '#1A1D2B', lineHeight: 1 }}>
              {fmtDay(dateStart)}
            </span>
            <span style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#8A8FA0' }}>
              {fmtMonth(dateStart)}
            </span>
            {endLabel && (
              <span style={{ display: 'block', fontSize: 9, fontWeight: 700, color: 'var(--color-primary, #1464B4)', marginTop: 2 }}>
                {endLabel}
              </span>
            )}
          </div>
        ) : <div style={{ flexShrink: 0 }} />}

        {/* Tag — truncates rather than overflows */}
        {tag && (
          <span style={{
            padding: '5px 10px',
            borderRadius: 14,
            background: 'var(--color-primary, #1464B4)',
            fontSize: 10,
            fontWeight: 800,
            color: '#fff',
            letterSpacing: '0.6px',
            textTransform: 'uppercase',
            alignSelf: 'flex-start',
            /* truncation */
            flexShrink: 1,
            minWidth: 0,
            maxWidth: dateStart ? 'calc(100% - 62px)' : '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {tag}
          </span>
        )}
      </div>

      {/* Bottom row: title + subtitle */}
      <div style={{ minWidth: 0 }}>
        <p style={{
          fontSize: 16,
          fontWeight: 800,
          color: '#fff',
          lineHeight: '21px',
          margin: 0,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}>
          {title}
        </p>
        {subtitle && (
          <p style={{
            fontSize: 12,
            color: 'rgba(255,255,255,0.78)',
            margin: '4px 0 0',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            📍 {subtitle}
          </p>
        )}
      </div>
    </div>
  );
}
